"use client";

import { useEffect, useState } from "react";
import { MILESTONES } from "@/lib/wikipedia";

interface MilestoneDialogProps {
  milestone: typeof MILESTONES[number] | null;
  onClose: () => void;
}

export function MilestoneDialog({ milestone, onClose }: MilestoneDialogProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [confetti, setConfetti] = useState<Array<{ id: number; left: number; color: string; delay: number }>>([]);

  useEffect(() => {
    if (milestone) {
      setIsVisible(true);
      // Generate confetti
      const newConfetti = Array.from({ length: 30 }, (_, i) => ({
        id: i,
        left: Math.random() * 100,
        color: ["#141beb", "#02eec4", "#ffd700", "#ff6b6b", "#4ecdc4"][Math.floor(Math.random() * 5)],
        delay: Math.random() * 2,
      }));
      setConfetti(newConfetti);
    }
  }, [milestone]);

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(onClose, 300);
  };

  if (!milestone) return null;

  return (
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center transition-opacity duration-300 ${
        isVisible ? "opacity-100" : "opacity-0 pointer-events-none"
      }`}
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={handleClose}
      />

      {/* Confetti */}
      {confetti.map((c) => (
        <div
          key={c.id}
          className="absolute top-0 w-3 h-3 rounded-full confetti"
          style={{
            left: `${c.left}%`,
            backgroundColor: c.color,
            animationDelay: `${c.delay}s`,
          }}
        />
      ))}

      {/* Dialog */}
      <div
        className={`relative bg-white rounded-2xl sm:rounded-3xl p-5 sm:p-8 max-w-sm sm:max-w-md mx-4 shadow-2xl transform transition-all duration-300 ${
          isVisible ? "scale-100 translate-y-0" : "scale-95 translate-y-4"
        }`}
      >
        {/* Prize icon */}
        <div className="text-5xl sm:text-7xl text-center mb-3 sm:mb-4 animate-bounce-slow">
          {milestone.icon}
        </div>

        {/* Title */}
        <h2 className="text-xl sm:text-2xl font-bold text-center gradient-text mb-1.5 sm:mb-2">
          🎉 Milestone Achieved! 🎉
        </h2>

        {/* Milestone name */}
        <h3 className="text-lg sm:text-xl font-semibold text-center text-[#141beb] mb-3 sm:mb-4">
          {milestone.name}
        </h3>

        {/* Description */}
        <p className="text-sm sm:text-base text-gray-600 text-center mb-4 sm:mb-6">
          {milestone.description}
        </p>

        {/* Article count */}
        <div className="bg-gradient-to-r from-[#141beb]/10 to-[#02eec4]/10 rounded-lg sm:rounded-xl p-3 sm:p-4 text-center mb-4 sm:mb-6">
          <span className="text-2xl sm:text-3xl font-bold gradient-text">
            {milestone.count}
          </span>
          <span className="text-sm sm:text-base text-gray-600 ml-2">articles read!</span>
        </div>

        {/* Close button */}
        <button
          onClick={handleClose}
          className="w-full py-2.5 sm:py-3 px-4 sm:px-6 bg-gradient-to-r from-[#141beb] to-[#02eec4] text-white font-semibold rounded-lg sm:rounded-xl shadow-lg hover:shadow-xl transform hover:scale-[1.02] transition-all duration-200 text-sm sm:text-base"
        >
          Keep Learning! 📚
        </button>
      </div>
    </div>
  );
}
